import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { AxiosError } from 'axios'

import { triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useDeleteDeliverableSet,
  useDeliverableSetsStore,
} from '@/features/deliverable-set'

const DeleteDeliverableSetDialog: React.FC = () => {
  const { t } = useTranslation()
  const {
    deletingDeliverableSet,
    deleteDialogOpen,
    setDeleteDialogOpen,
    clearDeletingDeliverableSet,
  } = useDeliverableSetsStore()

  // Delete mutation
  const deleteMutation = useDeleteDeliverableSet()

  const handleOpenChange = (newOpen: boolean) => {
    setDeleteDialogOpen(newOpen)
    if (!newOpen) {
      clearDeletingDeliverableSet()
      deleteMutation.reset()
    }
  }

  const handleConfirm = () => {
    if (!deletingDeliverableSet) return

    deleteMutation.mutate(
      {
        id: deletingDeliverableSet.id,
        projectId: deletingDeliverableSet.project_id,
      },
      {
        onSuccess: () => {
          triggerToast(
            'success',
            t('deliverableSet.dialog.messages.deleteSuccess')
          )
          setDeleteDialogOpen(false)
          clearDeletingDeliverableSet()
        },
        onError: (error: unknown) => {
          const axiosError = error as AxiosError<{ message?: string }>
          const errorMessage =
            axiosError.response?.data?.message ||
            axiosError.message ||
            t('deliverableSet.dialog.messages.errorGeneric')

          triggerToast('error', errorMessage)
        },
      }
    )
  }

  const isDeleting = useMemo(
    () => deleteMutation.isPending,
    [deleteMutation.isPending]
  )

  return (
    <Dialog open={deleteDialogOpen} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogContent className="sm:max-w-[480px] bg-white shadow-xl rounded-2xl p-6">
          <DialogHeader className="space-y-0.5">
            <DialogTitle className="m-0 font-semibold text-md text-primary-900">
              {t('deliverableSet.dialog.deleteTitle')}
            </DialogTitle>
            <DialogDescription className="text-sm text-tertiary-600">
              {t('deliverableSet.dialog.deleteDescription')}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex justify-stretch !space-x-0 gap-lg pt-3">
            <Button
              type="button"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid bg-white"
              variant="default"
              onClick={() => handleOpenChange(false)}
              disabled={isDeleting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid border-transparent !text-white !bg-error-solid"
              variant="default"
              onClick={handleConfirm}
              disabled={isDeleting}
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export default DeleteDeliverableSetDialog
